import { AddTwoToneIcon, Anchor, Button, DropdownSearch, GridIcon, GridItemLayout, GridLayout, KebabMenu, ListIcon, ListItemLayout, ListLayout, Pager, Searchfield, Table, TableColumn, TableRow, Textarea, ToggleButtonGroup, useMatchScreenWidth } from '@keplerco/core';
import React, { useEffect, useState } from 'react';
import styles from './roles.module.css';
import { useNavigate } from 'react-router-dom';
import { RoleSearchResultsDto } from '../../apis';
import { DefaultOptions, useApis } from '../../global-state';
import { SkeletonLoader } from '../../loading-handling/loaders/skeleton-loader/skeleton-loader';
import { SkeletonLoaderColumn, SkeletonLoaderRow } from '../../loading-handling/loaders/skeleton-loader/skeleton-loader.styles';
import { EmptyState } from '../../components/empty-state/empty-state';
import { HoverCard } from '../../components/hover-card/hover-card';
import { PagerNavigationConnector } from '../../components/pager-connector/pager-connector';
import { IRolesLayoutGridProps, IRolesLayoutListProps, RoleSearchRequest, ViewType } from './roles.models';
import { PagePath } from '../../navigation/navigation.enums';
export function RolesLayoutGrid({ loading, data }: IRolesLayoutGridProps): JSX.Element {
  const navigate = useNavigate();

  
  return (
    <React.Fragment>
      {loading ? (
        <RolesLayoutGridSkeleton />
      ) : (
        <GridLayout columnCount={3}>
          {data?.map(role => (
            <GridItemLayout key={role.name}>
              <HoverCard
                title={role.name!}
                description={role.description!}
                onClickAction={() => navigate(PagePath.role.replace(":roleId", role?.id!))}
              />
            </GridItemLayout>
          ))}
        </GridLayout>
      )}
    </React.Fragment >
  );
}

export function RolesLayoutList({ loading, setRequest, data }: IRolesLayoutListProps): JSX.Element {
  const navigate = useNavigate();

  return (
    <React.Fragment>
      {loading ? (
        <SkeletonLoaderRow style={{ marginBottom: 0 }}>
          <SkeletonLoaderColumn>
            <SkeletonLoader height="300px" />
          </SkeletonLoaderColumn>
        </SkeletonLoaderRow>
      ) : (
        <Table
          standAloneConfigColumn>
          {data?.map((role, index) => (
            <TableRow
              key={role.id}
              id={index}
              configCell={() => (
                <KebabMenu
                  index={index}
                  data={role}
                  items={[
                    { text: 'View Role', onClick: data => navigate(PagePath.role.replace(":roleId", data?.id!)) }
                  ]}
                />
              )}
            >
              <TableColumn sortable id="role" label="Role">
                <Anchor
                  hoverType="opacity"
                  textTransform="none"
                  onClick={() => alert(role.name)}
                >
                  {role.name}
                </Anchor>
              </TableColumn>

              <TableColumn id="description" label="Description">
                {role.description}
              </TableColumn>
            </TableRow>
          ))}
        </Table>
      )}
    </React.Fragment>
  );
}

export function RolesLayout(): JSX.Element {
  const [activeViewType, setActiveViewType] = useState<ViewType>('Grid');
  const [loading, setLoading] = useState<boolean>(true);
  const [request, setRequest] = useState<RoleSearchRequest>({
    search: undefined,
    category: undefined,
    pageSize: 6,
    page: 1
  });

  const { roleApi } = useApis();
  const [data, setData] = useState<RoleSearchResultsDto>();
  const [categories, setCategories] = useState<Array<string>>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('Select a category');
  useEffect(() => {
    async function loadCategories() {
      const response = await roleApi.roleCategoriesGet(DefaultOptions);
      setCategories(['Select a category', ...response]);
    }
    loadCategories();
  },
    []);
  useEffect(() => {
    async function getData() {
      setLoading(true);
      let category = request.category;
      if (category == 'Select a category') {
        category = '';
      }
      const response = await roleApi.roleSearchGet(
        request.search,
        category,
        request.pageSize,
        request.page, DefaultOptions);
      setData(response);
      setLoading(false);
    }
    getData();
  }, [request]);

  return (
    <div className={styles.rolesContainer}>
      <div className={styles.rolesActions}>
        <Searchfield onInput={search => setRequest({ ...request, search, page: 1 })} />

        <DropdownSearch
          label="Category"
          items={categories
            .map(category => ({
              value: category,
              label: category,
              onClick: () => {
                setSelectedCategory(category);
                setRequest({ ...request, category, page: 1 });
              },
              selected: selectedCategory === category,
            }))
          }
          dialogContained
        />

        <div className={styles.rolesActionsGroup}>
          <ToggleButtonGroup
            buttons={[
              { label: 'Grid', icon: <GridIcon /> },
              { label: 'List', icon: <ListIcon /> },
            ]}
            defaultButtonLabel={activeViewType}
            onClick={buttonLabel => setActiveViewType(buttonLabel as ViewType)}
          />
        </div>
      </div>

      {(loading || !!data?.results?.length) ? (
        <React.Fragment>
          {activeViewType === 'Grid' ? (
            <RolesLayoutGrid
              loading={loading}
              data={data?.results}
            />
          ) : (
            <RolesLayoutList
              loading={loading}
              setRequest={setRequest}
              data={data?.results}
            />
          )}

          {(!!data && data.totalPages! > 1) && (
            <PagerNavigationConnector
              defaultPageNumber={request.page}
              pageCount={data?.totalPages ?? 0}
              onPageChange={page => setRequest(currentState => ({ ...currentState, page }))}
            >
              {connector => (<Pager {...connector} isLoading={loading} />)}
            </PagerNavigationConnector>
          )}
        </React.Fragment>
      ) : (
        <EmptyState title="No roles found" />
      )}
    </div>
  );
}


function RolesLayoutGridSkeleton(): JSX.Element {
  const isMobile = useMatchScreenWidth('mobile');
  return (
    <React.Fragment>
      {isMobile ? (
        <GridLayout columnCount={3}>
          <GridItemLayout>
            <SkeletonLoader height="145px" />
          </GridItemLayout>

          <GridItemLayout>
            <SkeletonLoader height="145px" />
          </GridItemLayout>

          <GridItemLayout>
            <SkeletonLoader height="145px" />
          </GridItemLayout>

          <GridItemLayout>
            <SkeletonLoader height="145px" />
          </GridItemLayout>

          <GridItemLayout>
            <SkeletonLoader height="145px" />
          </GridItemLayout>

          <GridItemLayout>
            <SkeletonLoader height="145px" />
          </GridItemLayout>
        </GridLayout>
      ) : (
        <GridLayout columnCount={3}>
          <GridItemLayout>
            <SkeletonLoader height="250px" />
          </GridItemLayout>

          <GridItemLayout>
            <SkeletonLoader height="250px" />
          </GridItemLayout>

          <GridItemLayout>
            <SkeletonLoader height="250px" />
          </GridItemLayout>

          <GridItemLayout>
            <SkeletonLoader height="250px" />
          </GridItemLayout>

          <GridItemLayout>
            <SkeletonLoader height="250px" />
          </GridItemLayout>

          <GridItemLayout>
            <SkeletonLoader height="250px" />
          </GridItemLayout>
        </GridLayout>
      )}
    </React.Fragment>
  );
}
